import { ComponentType, ReactNode } from 'react';
import { Field as ReactFinalFormField } from 'react-final-form';
import { FormApi } from 'final-form';
import cx from 'classnames';
import { FormContext } from 'components/Form/Form';
import { FormLayoutContext } from 'layouts/FormLayout/FormLayout';

// `Field` hands these straight to whichever adapter `component` is, and the
// adapters call them with their own argument lists — downshift's
// `onInputValueChange(value)`, the multiselect's `onChangeCustom(item, input)`,
// `onRemoveItem(item, input)` — so a single-parameter signature cannot describe
// them.
type ChangeHandler = (...args: any[]) => void;
// Same reasoning: the checkbox adapter calls this with its own boolean.
type ClickHandler = (...args: any[]) => void;
type RenderFunction = (...args: unknown[]) => ReactNode;
/**
 * `value` is `any` rather than `unknown`: this wrapper does not know the field's
 * type, and under `strictFunctionTypes` an `unknown` parameter rejects every
 * real validator — one written as `(summary: string) => …` is not assignable,
 * because `unknown` cannot be passed where a `string` is expected.
 */
type FieldValidator = (
  value: any,
  allValues: object,
  meta?: unknown,
) => unknown;

interface ButtonProps {
  // `Selector` runs string labels through `String` and renders anything else
  // verbatim (its own propTypes say `node`), so JSX labels are allowed too.
  label: ReactNode;
  // Yes/no selectors key their buttons by the boolean they set.
  key: string | number | boolean;
  [key: string]: unknown;
}

/**
 * Options go straight to whichever adapter `component` is, and each reads its own
 * shape — `Dropdown` reads `{ key, value }`, `MultiSelectable` reads
 * `{ title, name }` — so this records the shapes in use instead of requiring one
 * of them.
 */
interface OptionProps {
  key?: string | number;
  value?: string;
  title?: string;
  name?: string;
  [key: string]: unknown;
}

type FieldProps = {
  name: string;
  /**
   * Whichever adapter this field renders. react-final-form accepts a component
   * or one of its three built-in input strings, and every adapter here is
   * untyped JS, so the props parameter cannot be narrowed at this boundary.
   */
  component: ComponentType<any> | 'input' | 'select' | 'textarea';
  label?: string;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  multiLineLabel?: boolean;
  textarea?: boolean;
  hidden?: boolean;
  nestedField?: boolean;
  fluid?: boolean | string;
  mode?: string;
  /**
   * Never read here — it passes through to whichever adapter `component` is, and
   * callers hand it react-final-form's own `FormApi`. The structural shape this
   * used to declare was a subset invented at this boundary.
   */
  form?: FormApi;
  featuresList?: unknown[];
  validate?: FieldValidator;
  firstSection?: boolean;
  buttons?: ButtonProps[];
  onChangeCustom?: ChangeHandler;
  useDatesFromValues?: boolean;
  withMarginTop?: boolean;
  applyDisabledStyle?: boolean;
  marginTop?: boolean;
  mask?: (value: string) => string;
  unmask?: (value: string) => string | number;
  grid?: boolean;
  gridSize?: string | number;
  verticalSpaceBetween?: boolean | string | number;
  type?: string;
  alignWithField?: boolean | string;
  options?: OptionProps[];
  mediaLabel?: string | ReactNode;
  checkboxProps?: Record<string, unknown>;
  size?: string;
  isSingleMedia?: boolean;
  disableWebcam?: boolean;
  customLabel?: string;
  availableItems?: unknown[];
  tagsToShow?: number | unknown[];
  selectedItem?: unknown;
  onInputValueChange?: ChangeHandler;
  inputValue?: string | number;
  onRemoveItem?: ChangeHandler;
  useOnChangeFormToHandle?: boolean;
  alignCenter?: boolean;
  max?: number;
  infinityObject?: Record<string, unknown>;
  onClickCustom?: ClickHandler;
  items?: unknown[];
  marginOnDesktop?: boolean;
  inputProps?: Record<string, unknown>;
  showTrashButton?: boolean;
  showCheckmark?: boolean;
  noBorder?: boolean;
  allowUserInput?: boolean;
  hideAutoCompleteIconOnMobile?: boolean;
  openMenuOnFocus?: boolean;
  defaultFileName?: string;
  isMultiUnit?: boolean;
  titleClassName?: string;
  responsive?: boolean;
  horizontalOnDesktop?: boolean;
  buttonClassName?: string;
  availabilityDate?: Date;
  timeZone?: string;
  coverageRegionId?: number;
  allowSchedulingFrom?: Date;
  viewOnly?: boolean;
  cleanOnMediaChange?: boolean;
  employee?: Record<string, unknown>;
  unit?: Record<string, unknown>;
  appointments?: unknown[];
  tagClassName?: string;
  min?: number;
  defaultValue?: unknown;
  renderLeft?: RenderFunction;
  renderRight?: RenderFunction;
  renderTop?: RenderFunction;
  renderThumbDetail?: RenderFunction;
  thumbContainerClassName?: string;
  disabledDays?: Date[] | Record<string, unknown>[];
  rootClassName?: string;
  defaultMonth?: Date | string | number;
  timePickerWrapperClassName?: string;
  sideInformationWrapperClassName?: string;
  wrapperClassName?: string;
  calendarRootClassName?: string;
  demandWrapperClassName?: string;
  fieldWrapperClassName?: string;
  itemClassname?: string;
  [key: string]: unknown;
};

function Field({
  hidden = false,
  nestedField = false,
  firstSection = false,
  withMarginTop = true,
  fieldWrapperClassName,
  ...rest
}: FieldProps) {
  return (
    <FormContext.Consumer>
      {(showErrors) => {
        return (
          <FormLayoutContext.Consumer>
            {(isInFormLayout) => {
              if (isInFormLayout) {
                return (
                  <div
                    className={cx(
                      {
                        'pt-sm': withMarginTop,
                        'pt-0': hidden || nestedField || firstSection,
                      },
                      fieldWrapperClassName,
                    )}
                  >
                    <ReactFinalFormField
                      {...rest}
                      name={rest.name}
                      ignoreError={!showErrors}
                      hidden={hidden}
                      newFieldComponent
                    />
                  </div>
                );
              }
              console.warn('All Fields should be in a FormLayout');
              return (
                <ReactFinalFormField
                  {...rest}
                  name={rest.name}
                  ignoreError={!showErrors}
                />
              );
            }}
          </FormLayoutContext.Consumer>
        );
      }}
    </FormContext.Consumer>
  );
}

export default Field;
